import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomPassword from 'components/shared/CustomPassword/CustomPassword';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import EntryLayoutScreen from 'components/shared/EntryLayout/EntryLayout';
import styles from './LoginScreen.module.css';

import { useInput } from 'customHook/useInput';
import { validatePassword } from 'helper/validate';
import RoutePaths from 'routes/RoutePaths';
import { getFirstAccessiblePath } from 'routes/utils';
import { AuthService } from 'service/auth.service';
import { useAppDispatch } from 'store/hooks';
import { setUser } from 'store/slices/authSlice';
import zeplinColor from 'theme/Color';
import { Constants } from 'utilities/constants';
import validator from 'validator';

const LoginScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const expiryTime = sessionStorage.getItem('loginExpiry') || '';
  sessionStorage.removeItem('reset-user');

  const {
    handleChange: handleEmailChange,
    handleBlur: handleEmailBlur,
    value: userName,
    invalid: invalidEmail,
    didChange: didEmailChange,
  } = useInput('', validator.isEmail);
  const {
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    value: password,
  } = useInput('', (text) => validatePassword(text).valid);

  const valid = !invalidEmail && password.length;

  const [error, setError] = useState('');
  const [errorPassword, setErrorPassword] = useState('');
  const [open, setOpen] = React.useState(false);
  const [alert, setAlert] = useState('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const login = async () => {
    try {
      handleOpen();
      const res = await AuthService.login({ userName, password });

      if (res.ChallengeName) {
        navigate(RoutePaths.CompleteAccount);
        console.log('first');
        const email = JSON.parse(res.ChallengeParameters.userAttributes).email;
        const session = res.Session;

        sessionStorage.setItem('challenge-session', session);
        sessionStorage.setItem('email', email);
      } else {
        dispatch(setUser(res));
        sessionStorage.setItem('full-name', res.fullName);
        sessionStorage.setItem('role', res.role);
        sessionStorage.setItem('cognitoSub', res.cognitoSub);

        sessionStorage.setItem('user-role', res.role);
        sessionStorage.setItem('organizationId', res?.organizationId ?? '');
        sessionStorage.setItem('organizationName', res?.organizationName ?? '');

        sessionStorage.setItem('current-user', userName);
        sessionStorage.setItem('user-token', JSON.stringify(res.AuthenticationResult));
        sessionStorage.setItem('SFToken', JSON.stringify(res.isSFLinked || res?.isSFLinked));
        sessionStorage.setItem('MSToken', JSON.stringify(res.isMSLogin));
        sessionStorage.setItem('isSlackConnected', res?.isSlackLinked);
        sessionStorage.setItem('isGoogleConnected', res?.isGoogleLinked);
        sessionStorage.setItem('isZoomConnected', res?.isZoomLinked);
        sessionStorage.setItem('user-permissions', JSON.stringify(res?.permissions ?? []));
        sessionStorage.setItem('is-super-admin', String(Boolean(res?.isSuperAdmin)));
        sessionStorage.setItem('is-admin', String(Boolean(res?.isAdmin)));
        sessionStorage.setItem('user-name', `${res.firstName}`);
        const now = new Date();
        const fiveHoursLater = new Date(now.getTime() + 60 * 60 * 1000 * 5);
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        sessionStorage.setItem('loginExpiry', fiveHoursLater.toString());
        sessionStorage.setItem('refreshToken', oneHourLater.toString());

        navigate(getFirstAccessiblePath(res?.permissions ?? [], Boolean(res?.isSuperAdmin)));
      }
    } catch (err: any) {
      const error = err?.response?.data?.name ? err?.response?.data?.name : err?.response?.data;
      if (err === 'Network Error') {
        setAlert('Network Error');
        setError('');
        setErrorPassword('');
      } else if (err?.response?.data) {
        setError(String(error));
        setErrorPassword('');
      } else if (error === Constants.ErrorType.NotAuthourized) {
        setErrorPassword(Constants.ErrorText.NotAuthorizedException);
        setError('');
      } else {
        setErrorPassword('');
        setError(Constants.ErrorText.UserNotFoundException);
      }
    } finally {
      handleClose();
    }
  };

  const children = (
    <div className={styles['login-input']}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 24 }}>
        <CustomTextField
          style={{ width: '50%', margin: 'auto' }}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
          name='userName'
          text={userName}
          error={error ? error : invalidEmail && didEmailChange ? 'Please enter valid email' : ''}
          label={'Email address'}
          type='email'
          tabindex={1}
        />
        <CustomPassword
          style={{ width: '50%', margin: 'auto' }}
          onBlur={handlePasswordBlur}
          onChange={handlePasswordChange}
          name='password'
          text={password}
          label='Password'
          error={errorPassword}
          tabindex={2}
        />
      </div>
      <div className={styles.helperlogin}>
        <Link to={RoutePaths.ForgotPassword}>
          <CustomButton
            disabled={false}
            variant='text'
            text={'Forgot Password?'}
            textStyle={{ color: zeplinColor.Brand1 }}
            onClick={() => {}}
            tabindex={3}
          />
        </Link>
      </div>
      <CustomButton
        disabled={valid ? false : true}
        onClick={login}
        variant='contained'
        color={valid ? 'primary' : 'customDisable'}
        style={{
          width: '50%',
          borderRadius: '14px',
          height: 56,
          '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
        }}
        textStyle={{ color: valid ? zeplinColor.Neutral1 : zeplinColor.Neutral8 }}
        text={'Log In'}
      />
    </div>
  );

  useEffect(() => {
    if (expiryTime) {
      if (new Date() < new Date(expiryTime)) {
        const existingPermissions = JSON.parse(sessionStorage.getItem('user-permissions') || '[]');
        const existingIsSuperAdmin = sessionStorage.getItem('is-super-admin') === 'true';
        navigate(getFirstAccessiblePath(existingPermissions, existingIsSuperAdmin));
      }
    }
  }, [navigate, expiryTime]);

  return (
    <EntryLayoutScreen
      alert={alert}
      header={'Log in'}
      subheader='Enter your email and password to continue.'
      input={children}
      footerText=''
      footerButtonText=''
      clickAction={() => {}}
      handleCloseModal={handleClose}
      open={open}
    />
  );
};

export default LoginScreen;
